import { useEffect, useRef, useState } from 'react';
import anime from 'animejs/lib/anime.es.js';

const VVS_LOGO = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/yqc71nna_FromNow2.jpg';

export default function IntroScreen({ onEnter }) {
  const overlayRef = useRef(null);
  const logoRef = useRef(null);
  const rippleRef = useRef(null);
  const enterRef = useRef(null);
  const [phase, setPhase] = useState('entering'); // entering | idle | exiting

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReduced) {
      // Skip animation, go directly to idle
      setPhase('idle');
      return;
    }

    // Animation sequence
    const tl = anime.timeline({
      easing: 'easeOutExpo',
      complete: () => setPhase('idle'),
    });

    // Step 1: Logo fade in + scale (0-600ms)
    tl.add({
      targets: logoRef.current,
      opacity: [0, 1],
      scale: [0.85, 1],
      duration: 600,
    });

    // Step 2: Ripple wave from center (500-1900ms)
    tl.add({
      targets: rippleRef.current,
      scale: [0, 8],
      opacity: [0.3, 0],
      duration: 1400,
      easing: 'easeOutCubic',
    }, 500);

    // Step 3: ENTER text fade in (1000-1400ms)
    tl.add({
      targets: enterRef.current,
      opacity: [0, 1],
      translateY: [10, 0],
      duration: 400,
    }, 1000);

    // Auto-dismiss after 5 seconds
    const autoDismiss = setTimeout(() => {
      if (phase === 'idle') {
        handleEnter();
      }
    }, 5000);

    // Safety timeout - force dismiss after 6 seconds
    const safetyTimeout = setTimeout(() => {
      handleEnter();
    }, 6000);

    return () => {
      clearTimeout(autoDismiss);
      clearTimeout(safetyTimeout);
    };
  }, []);

  const handleEnter = () => {
    if (phase === 'exiting') return;
    setPhase('exiting');

    anime({
      targets: overlayRef.current,
      opacity: [1, 0],
      duration: 400,
      easing: 'easeInExpo',
      complete: () => {
        localStorage.setItem('vvs_entered', 'true');
        onEnter();
      },
    });
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleEnter}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#050A14',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      data-testid="intro-screen"
    >
      {/* Logo */}
      <div style={{ position: 'relative' }}>
        <img
          ref={logoRef}
          src={VVS_LOGO}
          alt="VVS"
          style={{
            width: 'clamp(120px, 20vw, 200px)',
            height: 'clamp(120px, 20vw, 200px)',
            objectFit: 'cover',
            borderRadius: '50%',
            opacity: 0,
            boxShadow: '0 0 60px rgba(0,50,100,0.35), 0 0 120px rgba(0,30,70,0.2)',
          }}
        />
        
        {/* Ripple wave */}
        <div
          ref={rippleRef}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '100%',
            height: '100%',
            transform: 'translate(-50%, -50%) scale(0)',
            borderRadius: '50%',
            border: '1px solid rgba(0, 180, 255, 0.2)',
            pointerEvents: 'none',
            opacity: 0,
          }}
        />
      </div>

      {/* ENTER text */}
      <p
        ref={enterRef}
        style={{
          position: 'absolute',
          bottom: '15%',
          fontFamily: 'Space Grotesk, sans-serif',
          fontSize: '11px',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'rgba(148, 163, 184, 0.5)',
          opacity: 0,
        }}
        data-testid="intro-enter-btn"
      >
        Enter
      </p>
    </div>
  );
}
